import type { FootageLayout } from '../footage'

const clock = (epoch: number) => new Date(epoch * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' })
const length = (seconds: number) => `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`
const pct = (fraction: number) => `${fraction * 100}%`

interface Props {
  footage: FootageLayout
  // Index into footage.runs of the recording playing.
  playing: number
  moment: number
  onPick: (runId: number) => void
}

// FootageBar draws an incident's recordings along the span they cover, with
// the holes between them and where the incident's moment falls, and a button
// per recording — as mobile's clip view does (#279). The holes are footage
// nobody has; unsaid, a dispatcher reads the missing minute as a quiet one.
export function FootageBar({ footage, playing, moment, onPick }: Props) {
  const n = footage.runs.length
  return (
    <div role="group" aria-label="Footage" className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-base font-semibold text-zinc-100">Footage</h3>
        <span className="text-[13px] font-mono text-zinc-400 tabular-nums">
          {`${n} recordings · ${clock(footage.start)} – ${clock(footage.end)}`}
          {footage.missing > 0 && ` · ${footage.missing}s not recorded`}
        </span>
      </div>
      <div className="relative h-10 rounded-md border border-zinc-800 bg-zinc-900 overflow-hidden">
        {footage.runs.map((r, i) => (
          <span
            key={r.id}
            aria-hidden="true"
            style={{ left: pct(r.left), width: pct(r.width) }}
            className={`absolute top-1.5 bottom-1.5 min-w-1.5 rounded flex items-center pl-2 text-xs font-mono font-semibold ${
              i === playing ? 'bg-orange-400 text-zinc-950' : 'bg-orange-500/15 text-orange-300'
            }`}
          >
            {r.width > 0.06 ? i + 1 : ''}
          </span>
        ))}
        <span
          role="img"
          aria-label={`Incident moment ${clock(moment)}`}
          style={{ left: pct(footage.moment) }}
          className="absolute top-0 bottom-0 w-0.5 -ml-px bg-zinc-100"
        />
      </div>
      <div className="flex flex-wrap gap-2">
        {footage.runs.map((r, i) => (
          <button
            key={r.id}
            type="button"
            aria-pressed={i === playing}
            aria-label={`Recording ${i + 1} of ${n}${i === playing ? ', playing' : ''}`}
            onClick={() => onPick(r.id)}
            className={`h-10 px-3.5 flex items-center gap-2.5 rounded-md border text-sm transition-colors ${
              i === playing ? 'border-orange-400 bg-orange-500/10 text-zinc-100' : 'border-zinc-800 bg-zinc-900 text-zinc-100 hover:bg-zinc-800'
            }`}
          >
            <span className="font-semibold">Recording {i + 1}</span>
            <span className="font-mono text-[13px] text-zinc-400 tabular-nums">{`${clock(r.start)} · ${length(r.seconds)}`}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
